import React from 'react';
import { Modal, Button, Badge, Table } from 'react-bootstrap';
import { Chart } from '../types';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

interface ChartModalProps {
  chart: Chart | null;
  show: boolean;
  onHide: () => void;
}

const ChartModal: React.FC<ChartModalProps> = ({ chart, show, onHide }) => {
  const navigate = useNavigate();
  const [showToast, setShowToast] = React.useState(false);
  if (!chart) return null;

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      className="chart-modal"
      contentClassName="theme-card border-0 shadow-lg"
    >
      <Modal.Header closeButton className="gradient-bg-primary text-white border-0"
        style={{
          borderTopLeftRadius: '1.5rem',
          borderTopRightRadius: '1.5rem',
          padding: '2.2rem 2.2rem 1.2rem 2.2rem',
          minHeight: 90,
          background: 'linear-gradient(90deg, #4f8cff 0%, #6366f1 100%)'
        }}
      >
        <div className="w-100 d-flex justify-content-between align-items-center flex-wrap">
          <h2 className="fw-bold mb-0" style={{ letterSpacing: '-0.01em', fontSize: '2rem' }}>
            <span className="d-none d-md-inline">{chart.title}</span>
            <span className="d-md-none" style={{ fontSize: '1.5rem' }}>{chart.title}</span>
          </h2>
          <button
            className="btn btn-light border-0 shadow-sm px-3 py-2 d-flex align-items-center gap-2"
            style={{ borderRadius: '999px', fontWeight: 600, fontSize: '1.05rem', color: '#6366f1', background: '#f3f4f6' }}
            onClick={() => {
              const shareUrl = `${window.location.origin}/chart/${chart.id}`;
              if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(shareUrl);
                toast('차트 상세페이지 링크가 복사되었습니다!');
              } else {
                // Fallback for older browsers
                const textArea = document.createElement('textarea');
                textArea.value = shareUrl;
                document.body.appendChild(textArea);
                textArea.select();
                document.execCommand('copy');
                document.body.removeChild(textArea);
                toast('차트 상세페이지 링크가 복사되었습니다!');
              }
            }}
          >
            <i className="fas fa-share-alt"></i> 공유하기
          </button>
        </div>
      </Modal.Header>
      {showToast && (
        <div style={{ position: 'fixed', top: 80, left: '50%', transform: 'translateX(-50%)', zIndex: 9999 }}>
          <div className="alert alert-success shadow-sm mb-0" style={{ borderRadius: '999px', padding: '0.7em 2em', fontWeight: 600, fontSize: '1.1rem' }}>
            링크가 복사되었습니다!
          </div>
        </div>
      )}
      <Modal.Body style={{ padding: '2.2rem', background: '#fff', color: '#212529' }}>
        <div className="mb-4 p-4 rounded-4 shadow-sm bg-light d-flex align-items-center gap-3" style={{background: 'linear-gradient(90deg, #f8fafc 60%, #e0e7ef 100%)'}}>
          <i className="fas fa-info-circle fa-2x text-primary d-none d-md-block"></i>
          <i className="fas fa-info-circle text-primary d-md-none" style={{ fontSize: '1.2rem' }}></i>
          <span className="text-dark opacity-90 fw-semibold" style={{ lineHeight: 1.7 }}>
            <span className="d-none d-md-inline fs-5">{chart.description}</span>
            <span className="d-md-none" style={{ fontSize: '0.95rem' }}>{chart.description}</span>
          </span>
        </div>
        <div className="d-flex justify-content-end align-items-center mb-1" style={{ minHeight: '1.5rem' }}>
          <span className="text-muted small" style={{ fontSize: '0.98rem', letterSpacing: '-0.01em' }}>
            {chart.created_at ? new Date(chart.created_at).toLocaleDateString('ko-KR') : ''}
          </span>
        </div>
        <div className="table-responsive mb-4">
          {chart.items && chart.items.length > 0 && (
            <Table className="mb-0" hover style={{ borderRadius: '1rem', overflow: 'hidden', background: '#f9fafb' }}>
              <thead style={{ background: 'linear-gradient(90deg, #4f8cff 0%, #00e0ff 100%)', color: 'white', fontSize: '1.08rem' }}>
                <tr>
                  <th style={{ width: '10%' }}>순위</th>
                  <th style={{ width: '15%' }}>국기</th>
                  <th style={{ width: '25%' }}>이름</th>
                  <th style={{ width: '20%' }}>수치</th>
                  <th style={{ width: '30%' }}>설명</th>
                </tr>
              </thead>
              <tbody>
                {chart.items.map((item) => (
                  <tr key={item.id} style={{ verticalAlign: 'middle' }}>
                    <td className="text-center fw-bold">
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '0.38em 0.7em',
                          fontSize: '1rem',
                          fontWeight: 700,
                          lineHeight: 1,
                          color: 'white',
                          background: 'linear-gradient(90deg, #4f8cff 0%, #6366f1 100%)',
                          borderRadius: '999px',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
                          marginRight: '0.5em',
                          marginBottom: '0.25em',
                          letterSpacing: '-0.01em'
                        }}
                      >
                        {item.rank}
                      </span>
                    </td>
                    <td className="text-center fs-4">
                      {item.flag ? item.flag : ''}
                    </td>
                    <td className="fw-bold">
                      {item.name}
                    </td>
                    <td>
                      <span className="fw-bold text-primary">
                        {item.value}
                      </span>
                      {item.unit && (
                        <small className="text-muted ms-1">
                          {item.unit}
                        </small>
                      )}
                    </td>
                    <td className="text-muted small">
                      {item.details}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </div>
        {chart.keywords && chart.keywords.length > 0 && (
          <div className="mb-2">
            <h6 className="fw-bold mb-2 d-flex align-items-center gap-2" style={{ color: '#6366f1', fontSize: '1.1rem' }}>
              <i className="fas fa-hashtag text-primary"></i> 태그
            </h6>
            <div className="d-flex flex-wrap gap-2">
              {chart.keywords.map((keyword, index) => (
                <span
                  key={index}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.4em',
                    padding: '0.32em 1.1em',
                    fontSize: '1rem',
                    fontWeight: 600,
                    color: '#fff',
                    background: 'linear-gradient(90deg, #6366f1 0%, #4f8cff 100%)',
                    borderRadius: '999px',
                    cursor: 'pointer',
                    boxShadow: '0 2px 8px rgba(99,102,241,0.10)',
                    border: 'none',
                    transition: 'background 0.18s, transform 0.12s',
                  }}
                  onClick={() => {
                    onHide();
                    navigate(`/search?tag=${encodeURIComponent(keyword)}`);
                  }}
                  onMouseOver={e => {
                    e.currentTarget.style.background = 'linear-gradient(90deg, #4f8cff 0%, #6366f1 100%)';
                    e.currentTarget.style.transform = 'scale(1.07)';
                  }}
                  onMouseOut={e => {
                    e.currentTarget.style.background = 'linear-gradient(90deg, #6366f1 0%, #4f8cff 100%)';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                >
                  <i className="fas fa-tag me-1" style={{ fontSize: '0.95em', opacity: 0.85 }}></i>#{keyword}
                </span>
              ))}
            </div>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default ChartModal; 